import React, { useState, useRef } from 'react';
import { Phone, Mail, Send, CheckCircle, AlertTriangle } from 'lucide-react';
import { companyInfo } from '../data';

export default function LeadForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '' // Honeypot antispam field
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const formRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    // Check honeypot
    if (formData.website_url) {
      // Bot detected, pretend it succeeded but do nothing
      setTimeout(() => {
        setStatus('success');
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
      }, 1000);
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url
        }),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der skete en fejl under afsendelsen. Prøv igen.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Der skete en netværksfejl. Kontroller din forbindelse og prøv igen.');
    }
  };

  const focusForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstInput = formRef.current.querySelector('input');
      if (firstInput) firstInput.focus();
    }
  };

  return (
    <section id="kontakt" className="w-full bg-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 bg-white border border-slate-200 shadow-xl overflow-hidden">
        
        {/* Left Column: Focus Image */}
        <div className="relative min-h-[350px] lg:min-h-full bg-slate-900">
          <img
            src="https://assets.auxodev.dk/assets/154be17d-5a98-49b2-8816-0581c018ef0c/d0d8b5d7-8ea3-43db-8f2e-2643d430c3b7.webp"
            alt="Gravearbejde i Silkeborg, Brædstrup, Horsens og Skanderborg"
            className="absolute inset-0 w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/20 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-slate-950/10" />
        </div>

        {/* Right Column: Information & Interactive Lead Form */}
        <div className="flex flex-col">
          
          {/* Top Box: Text & Fast Actions */}
          <div className="p-8 sm:p-10 bg-slate-50 border-b border-slate-200 text-left">
            <h3 className="text-2xl font-extrabold text-slate-900 uppercase tracking-tight mt-1">
              Brug for en entreprenør? Ring i dag
            </h3>
            <p className="text-slate-600 text-sm mt-3 leading-relaxed">
              Har du brug for yderligere information eller ønsker du at få et uforpligtende tilbud på næste opgave? Så kontakt os allerede i dag, og lad os sammen finde den bedste løsning. Vi er med dig fra start til slut.
            </p>
            
            <div className="mt-6 flex flex-col sm:flex-row gap-4 sm:gap-8">
              <a
                href={`tel:${companyInfo.phoneFormatted}`}
                className="flex items-center gap-3 text-slate-900 hover:text-sky-600 transition-colors group"
              >
                <Phone className="w-5 h-5 text-slate-900 shrink-0" />
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ring direkte</div>
                  <div className="font-bold text-base">{companyInfo.phone}</div>
                </div>
              </a>

              <a
                href={`mailto:${companyInfo.email}`}
                className="flex items-center gap-3 text-slate-900 hover:text-sky-600 transition-colors group"
              >
                <Mail className="w-5 h-5 text-slate-900 shrink-0" />
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Send en mail</div>
                  <div className="font-bold text-base">{companyInfo.email}</div>
                </div>
              </a>
            </div>

            <button
              onClick={focusForm}
              className="mt-8 w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase text-xs tracking-wider py-3.5 px-6 transition-all duration-200 rounded-none text-center cursor-pointer"
            >
              Kontakt os for et godt tilbud
            </button>
          </div>

          {/* Bottom Box: The Quote Form */}
          <div ref={formRef} className="p-8 sm:p-10 bg-slate-950 text-white text-left flex-1 flex flex-col justify-center">
            <h4 className="text-lg font-extrabold uppercase tracking-wider text-white mb-6">
              Send os en besked
            </h4>

            {status === 'success' ? (
              <div className="bg-emerald-950/50 border border-emerald-500/30 p-6 text-center flex flex-col items-center justify-center space-y-3">
                <CheckCircle className="w-12 h-12 text-emerald-400" />
                <h5 className="font-bold text-emerald-400 uppercase tracking-wider text-sm">Besked sendt!</h5>
                <p className="text-slate-300 text-xs max-w-sm">
                  Mange tak for din henvendelse. Vi har modtaget din besked og vender tilbage til dig så hurtigt som muligt.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-4 text-xs font-bold uppercase tracking-wider text-sky-400 hover:text-sky-300 transition-colors"
                >
                  Send en ny besked
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot Field - Absolutely hidden from humans */}
                <div className="sr-only absolute -top-96 -left-96">
                  <label htmlFor="website_url">Website URL (Udfyld ikke dette felt)</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    autoComplete="off"
                    tabIndex="-1"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                      Navn <span className="text-orange-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Dit fulde navn"
                      className="w-full bg-slate-900 border border-slate-800 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 text-white text-sm px-4 py-3 rounded-none outline-hidden transition-all placeholder:text-slate-600"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                      Telefonnummer <span className="text-orange-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Dit telefonnummer"
                      className="w-full bg-slate-900 border border-slate-800 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 text-white text-sm px-4 py-3 rounded-none outline-hidden transition-all placeholder:text-slate-600"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                    E-mailadresse <span className="text-orange-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Din e-mailadresse"
                    className="w-full bg-slate-900 border border-slate-800 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 text-white text-sm px-4 py-3 rounded-none outline-hidden transition-all placeholder:text-slate-600"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                    Besked <span className="text-orange-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Beskriv din opgave her..."
                    className="w-full bg-slate-900 border border-slate-800 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 text-white text-sm px-4 py-3 rounded-none outline-hidden transition-all placeholder:text-slate-600 resize-none"
                  />
                </div>

                {status === 'error' && (
                  <div className="bg-red-950/50 border border-red-500/30 p-4 text-xs text-red-400 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                    <span>{errorMessage}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-slate-800 disabled:text-slate-500 text-white font-bold uppercase text-xs tracking-wider py-4 px-6 transition-all duration-200 rounded-none flex items-center justify-center gap-2 cursor-pointer"
                >
                  {status === 'submitting' ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sender besked...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      <span>Send besked</span>
                    </>
                  )}
                </button>
              </form>
            )}
          </div>

        </div>

      </div>
    </section>
  );
}
